import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { initSession, logActivity, resetSession } from '../utils/sessionManager';
import './HomePage.css';
import sample1 from '../assets/carouselSample/sample1.png';
import sample2 from '../assets/carouselSample/sample2.png';
import sample3 from '../assets/carouselSample/sample3.png';
import sample4 from '../assets/carouselSample/sample4.png';
import sample5 from '../assets/carouselSample/sample5.png';
import sample6 from '../assets/carouselSample/sample6.png';
import sample7 from '../assets/carouselSample/sample7.png';
import sample8 from '../assets/carouselSample/sample8.png';
import sample9 from '../assets/carouselSample/sample9.png';
import sample10 from '../assets/carouselSample/sample10.png';
import sample11 from '../assets/carouselSample/sample11.png';
import companyLogo from '../assets/companyLogo/ExpoTools Negative.png';

const HomePage = () => {
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const [carouselWidth, setCarouselWidth] = useState(0);
  const [x, setX] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  const [isNavigating, setIsNavigating] = useState(false);
  const [titleKey, setTitleKey] = useState(0);
  const prevProgressRef = useRef(0);

  // Carousel images
  const allImages = [sample1, sample2, sample3, sample4, sample5, sample6, sample7, sample8, sample9, sample10, sample11];

  const heroImagesRow1 = allImages;

  // Initialize session on mount
  useEffect(() => {
    const setupSession = async () => {
      // Reset any existing session when returning to home
      resetSession();
      // Start a new session
      await initSession();
      logActivity('homepage_visited');
    };
    setupSession();

    // Prevent browser back button from home page
    const handlePopState = (e) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
    };

    // Push initial state
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (carouselRef.current) {
        // Calculate width of one set of images
        const images = carouselRef.current.querySelectorAll('.carousel-image');
        if (images.length > 0) {
          const imageWidth = images[0].offsetWidth;
          const gap = parseFloat(getComputedStyle(carouselRef.current).gap) || 0;
          const setWidth = heroImagesRow1.length * imageWidth + (heroImagesRow1.length - 1) * gap;
          setCarouselWidth(setWidth);
        }
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [heroImagesRow1.length]);

  useEffect(() => {
    if (!isPaused && carouselWidth > 0) {
      let animationId;

      const animate = () => {
        const now = Date.now();
        const elapsed = (now - startTimeRef.current) / 1000;
        const progress = (elapsed / 40) % 1;

        // Check if carousel reset (progress wrapped around)
        if (progress < 0.1 && prevProgressRef.current > 0.9) {
          setTitleKey(prev => prev + 1);
        }
        prevProgressRef.current = progress;

        setX(-progress * carouselWidth);
        animationId = requestAnimationFrame(animate);
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        if (animationId) cancelAnimationFrame(animationId);
      };
    }
  }, [isPaused, carouselWidth]);

  const handleMouseEnter = () => {
    pauseTimeRef.current = Date.now();
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    // Calculate how much time passed while paused
    const pauseDuration = (Date.now() - pauseTimeRef.current) / 1000;
    // Adjust start time to account for pause
    startTimeRef.current += pauseDuration * 1000;
    setIsPaused(false);
  };

  const handleBeAHero = () => {
    if (isNavigating) {
      console.log('HomePage: Click blocked - already navigating');
      return; // Prevent double clicks
    }

    console.log('HomePage: Button clicked, navigating to select-superhero');
    setIsNavigating(true);

    // Log button click
    logActivity('become_legend_clicked');

    // Randomly select a transition animation
    const transitions = ['superman', 'flash'];
    const transition = transitions[Math.floor(Math.random() * transitions.length)];

    console.log('HomePage: Selected transition:', transition);

    logActivity('transition_selected', { transition });

    // Navigate directly to next page with transition
    navigate('/select-superhero', { state: { transition } });
  };

  return (
    <div className="homepage" onClick={handleBeAHero}>
      {/* Fixed Superhero Background */}
      <div className="superhero-background"></div>

      {/* Hero Section */}
      <div className="hero-section">
        <motion.h1
          key={titleKey}
          className="hero-title"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          SUPERHERO KIOSK
        </motion.h1>
        <motion.p
          className="hero-subtitle"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Unleash the hero within with <span className="company-highlight">IntelEvents</span>
        </motion.p>
        <motion.p
          className="hero-description"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Create your legendary superhero image
        </motion.p>
        <motion.button
          className="cta-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleBeAHero}
          disabled={isNavigating}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          TOUCH HERE TO START
        </motion.button>
      </div>

      {/* Simplified Carousel */}
      <div className="carousel-window">
        <motion.div
          ref={carouselRef}
          className="carousel-row"
          animate={{ x }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {[...heroImagesRow1, ...heroImagesRow1, ...heroImagesRow1].map((img, index) => (
            <div key={`row1-${index}`} className="carousel-image">
              <img src={img} alt={`Hero ${index + 1}`} />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Company Logo Footer */}
      <div className="company-logo-container">
        <img src={companyLogo} alt="Company Logo" className="company-logo" />
      </div>
    </div>
  );
};

export default HomePage;
